import React, { useContext, useRef } from "react"
import styled from "styled-components"
import gsap from "gsap"

import colors from "assets/styles/colors"
import text from "assets/styles/text"
import media from "assets/styles/media"
import { ReactComponent as PlaySVG } from "images/global/play.svg"
import { ScreenContext } from "components/Providers"

type PlayButtonProps = {
  onClick: VoidFunction
}

const PlayButton: React.FC<PlayButtonProps> = ({ onClick }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLParagraphElement>(null)
  const { tablet, mobile } = useContext(ScreenContext)
  const mouseMove = (e: MouseEvent) => {
    const clientRect = wrapperRef.current?.getBoundingClientRect()
    const x =
      e.clientX +
      (clientRect ? clientRect.width / 2 : 0) -
      ((clientRect ? clientRect.x : 0) +
        (clientRect ? clientRect.width / 2 : 0))
    const y =
      e.clientY +
      (clientRect ? clientRect.height / 2 : 0) -
      ((clientRect ? clientRect.y : 0) +
        (clientRect ? clientRect.height / 2 : 0))

    gsap.to(wrapperRef.current, {
      duration: 1,
      x: x,
      y: y,
    })
  }

  const onEnter = () => {
    if (!mobile && !tablet) {
      window.addEventListener("mousemove", mouseMove)

      gsap.to(buttonRef.current, {
        duration: 0.3,
        width: "6.944vw",
        height: "6.944vw",
      })
    }
  }

  const onLeave = () => {
    if (!mobile && !tablet) {
      window.removeEventListener("mousemove", mouseMove)

      gsap.to(wrapperRef.current, {
        duration: 1,
        x: 0,
        y: 0,
      })

      gsap.to(buttonRef.current, {
        duration: 0.5,
        width: "7.778vw",
        height: "7.778vw",
      })
    }
  }

  const handleClick = () => {
    if (!mobile && !tablet) {
      window.removeEventListener("mousemove", mouseMove)
    }
    onClick()
  }

  return (
    <ButtonWrapper
      ref={wrapperRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={handleClick}
    >
      <Button ref={buttonRef}>
        <Play />
      </Button>
      <Text ref={textRef}>Watch Now</Text>
    </ButtonWrapper>
  )
}

export default PlayButton

const ButtonWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  display: flex;
  align-items: center;
  z-index: 3;

  ${media.fullWidth} {
    bottom: 150px;
    right: 400px;
  }

  ${media.desktop} {
    bottom: 10.556vw;
    left: 60.347vw;
  }

  ${media.tablet} {
    left: 29.297vw;
    bottom: 32.422vw;
  }

  ${media.mobile} {
    bottom: 40.8vw;
    left: 5.07vw;
    right: auto;
  }
`

const Button = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 7.778vw;
  height: 7.778vw;
  border: 1px solid ${colors.black800};
  background-color: ${colors.base100};
  margin-right: 1.042vw;
  ${media.tablet} {
    width: 10.94vw;
    height: 10.94vw;
  }
  ${media.mobile} {
    width: 29.867vw;
    height: 29.867vw;
    margin-right: 4vw;
  }
`

const Play = styled(PlaySVG)`
  width: 1.736vw;
  height: 2.222vw;
  ${media.tablet} {
    width: 2.44vw;
    height: 3.13vw;
  }
  ${media.mobile} {
    width: 6.67vw;
    height: 8.53vw;
  }
`

const Text = styled.p`
  cursor: pointer;
  color: ${colors.white};
  ${text.desktop.buttonText}
  ${media.tablet} {
    ${text.tablet.buttonText}
  }
  ${media.mobile} {
    ${text.mobile.buttonText};
  }
`
